/**
 * Normalizarea campului „care clase" de pe activitatea practica (SP-05a).
 *
 * 🔴 CE FACE SI CE NU FACE. Normalizeaza pentru AFISARE — oglinda de sub camp, ca omul sa vada
 * traducerea inainte sa trimita. NU valideaza, si nu respinge nimic: sursa de adevar pentru ce e
 * o clasa valida e RASPUNSUL SERVERULUI, nu fisierul asta.
 *
 * De ce conteaza distinctia (directia S1): normalizatorul de pe server accepta IX-XII, pentru ca
 * listele ministerului aduc si licee. Vocabularul frontendului (`constants/classes.ts`) se oprea la
 * VIII; din 2026-10-24 merge si el pana la XII, dar regula ramane: o lista locala de permise ar fi
 * a doua sursa de adevar, si la prima divergenta un profesor ar primi o eroare **de la noi**
 * pentru ceva ce serverul accepta — si nimeni n-ar intelege de ce. De aceea aici nu exista nicio
 * lista de permise: intelegem ce putem, restul pleaca la server asa cum a fost scris, iar serverul
 * spune ce n-a inteles.
 *
 * 🔴 CLASA PREGATITOARE, si capcana care o sterge tacut. Canonicul ei e `0 A`. In JavaScript `0`
 * e **falsy**: orice `if (an)`, `filter(Boolean)`, `an || implicit` sau trecere prin `Number(...)`
 * urmata de un test de adevar o scoate din lista **fara nicio eroare**. Consecinta nu e cosmetica:
 * numarul de clase inmulteste punctajul cu 50, deci profesorul scrie trei clase si primeste puncte
 * pentru doua. Owner-ul a decis (2026-09-06) ca pregatitoarea SE PUNCTEAZA, deci pierderea ei e un
 * defect de puncte, nu o omisiune de afisare. Anul se tine ca STRING peste tot in fisierul asta,
 * si nu se compara niciodata prin adevar.
 *
 * Eticheta ei pe ecran e «Pregătitoare A», nu „0 A" — „0 A" nu e cum vorbeste nimeni. Valoarea
 * canonica ramane `0 A`, si aia se trimite.
 */

/** Anii, in ordinea scolara. Indexul e si ordinea de sortare a etichetelor. */
const ANI_ROMANI = ['0', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

/**
 * Cifra araba -> an roman. `0` e inclus deliberat, si de aceea harta e un obiect cu chei string:
 * un `Array` indexat ar fi invitat la `ani[n] || ...`, adica exact capcana de mai sus.
 */
const ARABE: Record<string, string> = {
    '0': '0', '1': 'I', '2': 'II', '3': 'III', '4': 'IV', '5': 'V', '6': 'VI',
    '7': 'VII', '8': 'VIII', '9': 'IX', '10': 'X', '11': 'XI', '12': 'XII',
};

/** Cum scriu oamenii clasa pregatitoare. Toate merg spre anul `0`. */
const PREGATITOARE = ['pregatitoare', 'pregătitoare', 'preg', 'p'];

export interface ClasaCanonica {
    /** Valoarea trimisa la server: `V A`, `0 A`, `VIII` (an fara litera). */
    canonic: string;
    /** Ce se scrie pe ecran: «Pregătitoare A» pentru `0 A`, altfel identic cu `canonic`. */
    eticheta: string;
}

export interface RezultatClase {
    /** Ce am inteles, in ordinea scolara, fara duplicate. */
    clase: ClasaCanonica[];
    /** Ce n-am putut citi, exact cum a scris omul. NU e o eroare aici — serverul decide. */
    neintelese: string[];
}

/**
 * Separatorii pe care ii folosesc profesorii: virgula, punct si virgula, bara, rand nou, si „si".
 *
 * 🔴 „si" se delimiteaza cu SPATII, nu cu `\b`. Forma dinainte era `\bși\b`, si nu se potrivea
 * NICIODATA: `\b` e ASCII si in JavaScript, la fel ca in Java, deci `ș` nu e caracter „de cuvant"
 * si nu exista granita intre un spatiu si el. Rezultatul masurat: `5A si 5B` mergea, iar
 * `5A și 5B` — romana scrisa CORECT — cadea intreg in `neintelese`. Profesorul care scrie mai
 * ingrijit era penalizat.
 *
 * Gasit prin ANALOGIE, nu prin masuratoare proprie: B1 a raportat exact acelasi defect in
 * `ClaseCanonice.java` (2026-09-06), reparat la el cu `UNICODE_CHARACTER_CLASS`. Am verificat
 * imediat aceeasi constructie la mine si era rupta la fel. **Un defect raportat de partea
 * cealalta a contractului merita cautat si la tine, cand mecanismul e acelasi** — aici a fost
 * acelasi `\b` peste acelasi cuvant, in doua limbaje.
 *
 * `şi` cu sedila (U+015F, forma veche din Windows) e inclus deliberat: se tasteaza inca.
 */
const SEPARATORI = /\s*(?:,|;|\/|\n|\r)\s*|\s+(?:si|și|şi)\s+/i;

/**
 * Un fragment: an (roman sau arab, sau pregatitoare) + litera optionala.
 *
 * Acopera formele masurate in directia S1: `5A`, `5 A`, `V A`, `a V-a A`, `cls. V B`, `Va`,
 * `5.A`, `clasa a V-a`, `pregatitoare A`. Prefixele („clasa", „cls.", „a") si sufixul „-a" se
 * curata inainte, nu se descriu in regex — altfel expresia devine nedescifrabila.
 */
const GUNOI_LA_START = /^(?:clasa|clas|cls|cl)\.?\s*/i;
const ARTICOL = /^a\s+/i;
/**
 * 🔴 Ordinalul LIPIT: `aVIIa` → `VII`. Are regex propriu pentru ca `ARTICOL` cere spatiu dupa „a"
 * si nu-l prinde.
 *
 * Nu e o forma exotica — e chiar linia pe care backendul o are de la inceput
 * (`ClaseCanonice.java`: `text.replaceAll("^a([ivx]+)a\b", "$1 ")`, cu comentariul „fara linia
 * asta clasa ar fi raportata ca nerecunoscuta desi omul a scris-o perfect inteligibil").
 *
 * ⚠️ Lipsa ei aici nu producea o eroare, ci un CONTOR MAI MIC: `aVIIa B` cadea in `neintelese`,
 * deci oglinda arata cinci clase acolo unde serverul numara sase — iar numarul de sub camp e
 * chiar cel care se inmulteste cu 50. Profesorul ar fi vazut o clasa scrisa corect „lipsind" si
 * ar fi „reparat" ce era bun. Gasit de S1 tastand sase forme deodata (2026-09-06).
 *
 * 🔴 Si e o lectie despre sonda diferentiala, nu doar despre regex: cele 23 de cazuri au trecut
 * pentru ca le-am scris EU, din ce stia normalizatorul meu sa faca. O sonda care compara doi
 * normalizatori pe intrarile alese de unul dintre ei masoara mai putin decat pare.
 */
const ORDINAL_LIPIT = /^a([ivx]+)a\b/i;
/**
 * Sufixul ordinal romanesc, DOAR in forma cu cratima: „V-a" → „V".
 *
 * 🔴 A fost `/-?a\b/i`, adica si fara cratima — si atunci `5A` isi pierdea litera („5A" → „5"),
 * pentru ca `anCanonic` e folosita si ca oracol de validitate. Sase teste au picut deodata cand
 * am adaugat verificarea pe sirul intreg; cauza era aici, nu acolo. Cratima e obligatorie.
 */
const SUFIX_A = /-a$/i;

function curataFragment(brut: string): string {
    let s = brut.trim().replace(/\./g, ' ').replace(/\s+/g, ' ').trim();
    s = s.replace(GUNOI_LA_START, '').trim();
    s = s.replace(ORDINAL_LIPIT, '$1 ').replace(/\s+/g, ' ').trim();
    s = s.replace(ARTICOL, '').trim();
    return s;
}

/**
 * Desparte partea de an de litera. Litera e o singura litera la final, precedata sau nu de spatiu.
 * `Va` -> `V` + `a`; `VIII` -> `VIII` + fara litera; `0 A` -> `0` + `A`.
 */
function despartite(s: string): { an: string; litera: string | null } {
    const cuSpatiu = /^(.*?)\s+([A-Za-zĂÂÎȘȚăâîșț])$/.exec(s);
    if (cuSpatiu) return { an: cuSpatiu[1].trim(), litera: cuSpatiu[2] };

    // 🔴 INTREGUL sir, ca an, INAINTEA oricarei taieri. `VIII` e un an valid; daca ne apucam sa
    //    taiem ultima litera, ramane `VII` — tot valid! — plus litera `I`, deci `VIII` devine
    //    tacut `VII I`, adica ALT an, cu eticheta plauzibila. Conditia „ce ramane e citibil ca an"
    //    nu apara de asta, pentru ca si ce ramane e citibil; singura aparare e sa incerci intai
    //    forma intreaga. Prins de test, nu de citit: comentariul de aici sustinea ca e acoperit
    //    inainte sa fie.
    if (anCanonic(s) !== null) return { an: s, litera: null };

    // Fara spatiu: `5A`, `Va`, `IXb`. Se taie ultima litera doar daca ce ramane e citibil ca an.
    const fara = /^(.*[^\s])([A-Za-zĂÂÎȘȚăâîșț])$/.exec(s);
    if (fara && anCanonic(fara[1]) !== null) return { an: fara[1].trim(), litera: fara[2] };

    return { an: s, litera: null };
}

/**
 * Anul in forma canonica, sau `null` daca nu se poate citi.
 *
 * ⚠️ Intoarce `'0'` ca STRING pentru pregatitoare. Nu schimba tipul in `number`: apelantii testeaza
 * rezultatul cu `!== null`, iar cu `number` un `0` ar cadea pe orice test de adevar scris din
 * reflex, si clasa ar disparea din contor. Vezi comentariul din capul fisierului.
 */
function anCanonic(brut: string): string | null {
    const s = brut.trim().replace(SUFIX_A, '').trim();
    if (s === '') return null;

    const jos = s.toLowerCase();
    if (PREGATITOARE.includes(jos)) return '0';

    if (/^\d+$/.test(s)) {
        const dinArabe = ARABE[String(Number(s))];
        return dinArabe ?? null;
    }

    const sus = s.toUpperCase();
    return ANI_ROMANI.includes(sus) ? sus : null;
}

/**
 * Litera clasei, fara diacritice: `Ă` → `A`, `Ș` → `S`, `Ț` → `T`, `Î`/`Â` → `I`/`A`.
 *
 * 🔴 Masurat pe DEV, nu presupus (V1, 2026-09-06): `Pregătitoare Ă` intra si iese din server ca
 * **`0 A`** — 201, `numar_clase = 1`. Inainte de reparatia asta, oglinda arata profesorului `0 Ă`
 * si in baza ajungea `0 A`: nimeni nu lua eroare, dar ce vedea omul nu era ce se salva. Exact
 * defectul pentru care exista oglinda, produs chiar de ea.
 *
 * ⚠️ Si masuratoarea aia acopera mai putin decat pare, ceea ce e mai important decat lista de
 * litere neverificate. S-a masurat **o** litera (`Ă`) pe **un** cuvant (`Pregătitoare`) — deci nu
 * stim daca serverul plieaza diacriticele in LITERA clasei, sau daca ce am vazut e calea
 * cuvantului-cheie „pregatitoare", care are oricum propriul dictionar. Sunt doua ipoteze, si
 * prima nu se transfera pe a doua (precizarea lui V1, 2026-09-06, dintr-o greseala identica pe
 * care o facuse in aceeasi zi pe alt capitol).
 *
 * `sonda-clase-asteptari.json` are cazul care le desparte — `V Ă`: aceeasi litera, alt an. Pana
 * la el, `Â`, `Î`, `Ș`, `Ț` raman deschise **fiecare separat**, nu ca un grup dedus dintr-una.
 * Testele de mai jos le fixeaza ca sa nu driveze, dar un test scris de acelasi om care a scris
 * regula nu e o masuratoare — e coerenta cu sine.
 */
function literaFaraDiacritice(litera: string): string {
    return litera.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase();
}

function eticheteaza(an: string, litera: string | null): ClasaCanonica {
    const canonic = litera ? `${an} ${litera}` : an;
    // 🔴 Comparatie pe STRING, nu pe adevar: `an === '0'`, niciodata `!an`.
    const numeAn = an === '0' ? 'Pregătitoare' : an;
    return { canonic, eticheta: litera ? `${numeAn} ${litera}` : numeAn };
}

/**
 * Traduce ce a scris omul in etichete canonice.
 *
 * Duplicatele se contopesc in tacere (`5A, 5 a, V A` -> o singura eticheta `V A`): omul a spus
 * acelasi lucru de trei ori, nu e o greseala si nu merita mesaj.
 */
export function normalizeazaClase(intrare: string): RezultatClase {
    const fragmente = (intrare ?? '')
        .split(SEPARATORI)
        .map((f) => f.trim())
        .filter((f) => f.length > 0);

    const vazute = new Set<string>();
    const clase: ClasaCanonica[] = [];
    const neintelese: string[] = [];

    for (const brut of fragmente) {
        const { an, litera } = despartite(curataFragment(brut));
        const canonicAn = anCanonic(an);

        // 🔴 `!== null`, NU `if (canonicAn)`: pregatitoarea intoarce `'0'`, care e falsy ca numar
        //    si adevarat ca string nevid — dar o comparatie explicita nu depinde de care din ele e.
        if (canonicAn === null) {
            neintelese.push(brut);
            continue;
        }

        const clasa = eticheteaza(canonicAn, litera ? literaFaraDiacritice(litera) : null);
        if (vazute.has(clasa.canonic)) continue;
        vazute.add(clasa.canonic);
        clase.push(clasa);
    }

    /**
     * 🔴 ORDINEA E A OMULUI — prima aparitie, ca pe server (`LinkedHashSet`, regula 11 din
     * `clase-canonice-forme-acceptate.md`). Aici a fost o sortare in ordine scolara, pusa de mine
     * fara sa fie ceruta; scoasa la ratificarea lui S1 (2026-09-06), din trei motive, in ordinea
     * greutatii lor:
     *
     * 1. **Interactiune.** La stergere se rescrie textul din etichete. Cu sortare, apasand `×`
     *    textul omului **se rearanjeaza sub degetele lui** — a cerut o stergere si a primit o
     *    rescriere. Aceeasi familie cu „campul care se goleste la eroare", doar mai blanda.
     * 2. **Rolul oglinzii.** Ea exista ca omul sa VERIFICE ce am inteles, citind in paralel:
     *    ochiul merge pe textul lui si pe etichete, in aceeasi ordine. Sortarea rupe paralelismul
     *    exact cand e nevoie de el — la multe clase. La putine, nu aducea nimic.
     * 3. **Acordul cu serverul** e al treilea si cel mai slab motiv — dar gratuit. Ordinea nu
     *    schimba punctajul (numarul e al unei multimi), doar ca ar fi fost inca un loc in care
     *    ecranul spune altceva decat sursa de adevar.
     *
     * ⚠️ Ce NU e motiv: „serverul face asa, deci si noi". Daca sortarea ar fi fost mai buna pentru
     * om, o pastram si acceptam divergenta — oglinda e previzualizare, nu reproducere.
     */
    return { clase, neintelese };
}

/**
 * Sirul trimis la server: canonicele, separate prin virgula.
 *
 * ⚠️ Se trimite DOAR ce am inteles? Nu — se trimite ce a scris omul, neatins, pentru ca serverul
 * are ultimul cuvant si trebuie sa poata numi fragmentele lui. Functia asta exista pentru
 * afisare si pentru teste, nu pentru corpul cererii.
 */
export function canoniceCaText(rezultat: RezultatClase): string {
    return rezultat.clase.map((c) => c.canonic).join(', ');
}

/**
 * Textul campului dupa stergerea unei etichete.
 *
 * 🔴 Pastreaza `neintelese`. Fara ele, «a V-a A, gradinita» devine sir GOL la apasarea lui `×`,
 * si „gradinita" dispare fara ca nimeni s-o ceara — exact ce interzice directia, doar ca pe calea
 * butonului, nu a erorii. Si in cel mai prost moment: fragmentul neinteles e chiar textul de care
 * omul are nevoie ca sa repare.
 *
 * Sta aici, nu in componenta, ca sa poata fi TESTAT: testele de componenta nu ruleaza in proiect
 * (`vitest.config.ts` e pe `environment: 'node'`, fara jsdom). O regula care se poate pierde tacut
 * si n-are cum sa fie prinsa nu trebuie sa stea intr-un loc netestabil.
 */
export function textDupaStergere(rezultat: RezultatClase, canonicSters: string): string {
    const ramase = rezultat.clase
        .filter((c) => c.canonic !== canonicSters)
        .map((c) => c.canonic);
    return [...ramase, ...rezultat.neintelese].join(', ');
}

/**
 * Textul pregatit PENTRU SERVER: sedila pliata la virgula-jos.
 *
 * 🔴 `ş`/`ţ` (U+015F / U+0163, sedila — forma veche, frecventa in documente lipite) si
 * `ș`/`ț` (U+0219 / U+021B, virgula-jos — forma corecta) sunt **indistinctibile pe ecran**, dar
 * nu si pentru server: azi el accepta doar virgula-jos. Masurat de B1 pe DEV, si verificat de V1
 * pe octeti (`È` vs `Å`).
 *
 * Fara plierea asta, un text lipit dintr-un document vechi trecea de oglinda — care arata frumos
 * doua clase — si pica cu **400 pe tot sirul**. Cea mai proasta combinatie posibila: interfata
 * promite, serverul refuza, si omul n-are cum sa vada de ce.
 *
 * ⚠️ E o punte peste o fereastra de divergenta, nu o regula permanenta: B1 repara plierea pe
 * server (varianta 2, inainte de despartire), si dupa aia functia asta devine inofensiva — nu
 * gresita, doar redundanta. Omul nu trebuie sa plateasca ordinea in care ne-am reparat noi codul.
 *
 * NU se aplica la afisare: pe ecran omul vede exact ce a scris.
 */
export function pentruServer(text: string): string {
    return text
        .replace(/ş/g, 'ș').replace(/Ş/g, 'Ș')
        .replace(/ţ/g, 'ț').replace(/Ţ/g, 'Ț');
}
